import { logger } from '@fe/core';
import { MousePoint } from '../types';
import { Rect } from '../util';
import { Axis } from './axis';
import { ChartLayerSpecification, ChartProps, ChartQueryFeature, DataSourceSpecification, ScaleSpecification } from './chart-props';
import { drawAxisIndicatorLayer } from './draw/drawAxisIndicatorLayer';
import { drawAxisLabelsLayer } from './draw/drawAxisLabelsLayer';
import { drawCircleLayer } from './draw/drawCircleLayer';
import { drawGridLinesLayer } from './draw/drawGridLinesLayer';
import { drawIconGridLayer } from './draw/drawIconGridLayer';
import { drawIconLayer } from './draw/drawIconLayer';
import { drawLineLayer } from './draw/drawLineLayer';
import { drawRasterLayer } from './draw/drawRasterLayer';
import { drawRectangleLayer } from './draw/drawRectangleLayer';
import { drawShadeLayer, drawShadeLineLayer } from './draw/drawShadeLayer';
import { drawTextLayer } from './draw/drawTextLayer';
import { createDrawer, Drawer } from './drawer';
import { Evented } from './evented';
import { Placement } from './placement/placement';
import { RasterSource } from './source/raster-source';
import { Source } from './source/source';
import { VectorSource } from './source/vector-source';
import { Transform } from './transform';

export type DrawPass = {
    viewState: ViewState;
    drawer: Drawer;
    placement: Placement;
    isRendering: boolean;
    getFont: (size: number | null | undefined, font?: string | null) => string;
};

export type ViewState = {
    scaleX: number;
    scaleY: number;
    zoom: [number, number];
    translateX: number;
    translateY: number;
    canvasWidth: number;
    canvasHeight: number;
    pixelRatio: number;
    chartViewport: Rect;
    chartArea: Rect;
    leftScaleArea: Rect;
    topScaleArea: Rect;
    rightScaleArea: Rect;
    bottomScaleArea: Rect;
};

const MAX_ZOOM = 6;
const MIN_ZOOM = 1;
const ZOOM_SPEED_PER_PIXEL = 1 / 200;

export class Chart extends Evented {
    private readonly mediaQuery = window.matchMedia(`(resolution: ${window.devicePixelRatio}dppx)`);
    private drawer: Drawer;
    private container: HTMLElement;
    public canvas: HTMLCanvasElement;
    private observer: ResizeObserver;
    private requestedFrame: number | null = null;
    private axes: Partial<Record<string, Axis>> = {};
    private sources: Partial<Record<string, Source>> = {};
    private layers: ChartLayerSpecification[] = [];
    private layersState: Partial<Record<string, any>> = {};
    private featureState: Partial<Record<string, Partial<Record<string, any>>>> = {};
    private lastPlacementTime = 0;
    private readonly placement = new Placement();
    private props: ChartProps;
    private sourcesDirty = false;
    private layersDirty = false;
    private propsChanged = false;
    private mouseIsDown = false;
    private mouseDragging = false;
    private readonly transform: Transform = new Transform();

    constructor(containerId: string, props: ChartProps) {
        super();
        this.props = props;
        const container = document.getElementById(containerId);
        if (!container) {
            throw new Error('Parent container not found.');
        }

        this.container = container;
        this.canvas = this.createCanvas();
        this.observer = new ResizeObserver(this.resize.bind(this));
        this.observer.observe(this.canvas);
        this.addListeners(this.canvas);
        this.drawer = createDrawer(this.canvas);
        this.resize();
    }

    setProps(props: ChartProps) {
        this.props = props;
        this.propsChanged = true;
        this.triggerRepaint();
    }

    setScales(scales: Record<string, ScaleSpecification>) {
        for (const key in scales) {
            if (key in this.axes) {
                this.axes[key]!.setProps(scales[key]);
            } else {
                this.axes[key] = new Axis(scales[key]);
            }
        }
    }

    setLayers(layers: ChartLayerSpecification[]) {
        this.layersDirty = true;
        this.layers = layers;
        // Should only delete layers that have changed but since we don't know which layers have changed just delete the whole thing
        this.layersState = {};
        this.featureState = {};
    }

    upsertSource(id: string, data: DataSourceSpecification) {
        this.sourcesDirty = true;
        let source = this.sources[id] as Source | null;

        if (!source) {
            source = data.type === 'vector' ? new VectorSource(id) : new RasterSource(id);
            this.sources[id] = source;
        }

        if (source instanceof VectorSource && data.type === 'vector') {
            source.setData(data.features);
        } else if (source instanceof RasterSource && data.type === 'raster') {
            source.setData(data);
        }

        this.featureState = {};
        this.triggerRepaint();
    }

    removeSource(id: string) {
        this.sourcesDirty = true;
        delete this.sources[id];
    }

    removeAllData(): void {
        this.sourcesDirty = true;
        this.sources = {};
    }

    triggerRepaint() {
        if (this.requestedFrame === null) {
            this.requestedFrame = requestAnimationFrame((timestamp) => {
                this.requestedFrame = null;
                this.render(timestamp);
            });
        }
    }

    setFeatureState(sourceId: string, featureId: string, state: any) {
        this.featureState[sourceId] ??= {};
        this.featureState[sourceId][featureId] = state;
        this.triggerRepaint();
    }

    removeFeatureState(layerId: string, featureId: string, key: string) {
        if (layerId in this.featureState) {
            if (featureId in this.featureState[layerId]!) {
                delete this.featureState[layerId]![featureId][key];
                this.triggerRepaint();
            }
        }
    }

    private render(frameTimestamp: number) {
        if (this.sourcesDirty || this.layersDirty || this.propsChanged) {
            this.layersDirty = false;
            this.propsChanged = false;
            this.applyAxisAutoLimits();
            this.applyAxisMarginAndSize();
        }

        this.transform.update();

        if (this.transform.isMoving()) {
            this.triggerRepaint();
        }

        this.draw(frameTimestamp);

        // Clear sources after draw because layers need to know about it
        if (this.sourcesDirty) {
            this.sourcesDirty = false;

            for (const key in this.sources) {
                this.sources[key]!.clearDirty();
            }
        }
    }

    private draw(frameTimestamp: number): void {
        const state = this.transform.getState();
        const deltaT = frameTimestamp - this.lastPlacementTime;

        if (deltaT > 100) {
            this.placement.clear();
            const pass: DrawPass = {
                getFont: this.getFont,
                viewState: state,
                isRendering: false,
                drawer: this.drawer,
                placement: this.placement
            };

            for (const layer of this.layers) {
                if (layer.visible === false) {
                    continue;
                }

                const source = this.sources[layer.sourceId];
                if (!source) {
                    continue;
                }

                const xAxis = this.axes[layer.xAxisId];
                const yAxis = this.axes[layer.yAxisId];
                if (!xAxis || !yAxis) {
                    continue;
                }

                if (source instanceof VectorSource) {
                    const features = source.getData();
                    switch (layer.type) {
                        case 'text':
                            drawTextLayer(pass, features, layer, xAxis, yAxis, this.featureState[source.id], this.layersState[layer.id]);
                            break;
                    }
                }
            }

            this.placement.place();
            this.lastPlacementTime = frameTimestamp;
        }
        const pass: DrawPass = {
            getFont: this.getFont,
            viewState: state,
            isRendering: true,
            drawer: this.drawer,
            placement: this.placement
        };

        this.drawer.setScale(state.pixelRatio, state.pixelRatio);
        this.drawer.clear();
        this.drawer.setFont(this.getFont(null));
        this.drawer.drawRect(pass.viewState.chartViewport, { color: this.props.background.color });

        for (const key in this.axes) {
            const axis = this.axes[key]!;
            if (axis.getProps().hideWhenNoData) {
                // TODO: probably cache this inside axis class
                const hasData = this.layers
                    .filter((x) => x.xAxisId === key || x.yAxisId === key)
                    .some((x) => this.sources[x.sourceId]?.hasData() === true);
                if (!hasData) {
                    continue;
                }
            }
            this.axes[key]!.draw(pass);
        }

        pass.drawer.beginClip(state.chartViewport);
        for (const layer of this.layers) {
            if (layer.visible === false) {
                continue;
            }

            const source = this.sources[layer.sourceId];
            if (!source) {
                continue;
            }

            const xAxis = this.axes[layer.xAxisId];
            const yAxis = this.axes[layer.yAxisId];
            if (!xAxis || !yAxis) {
                continue;
            }

            if (source instanceof VectorSource) {
                const features = source.getData();
                switch (layer.type) {
                    case 'line':
                        drawLineLayer(pass, features, layer, xAxis, yAxis, this.featureState[source.id], this.layersState[layer.id]);
                        break;
                    case 'grid-lines':
                        drawGridLinesLayer(pass, features, layer, xAxis, yAxis, this.featureState[source.id], this.layersState[layer.id]);
                        break;
                    case 'circle':
                        drawCircleLayer(pass, features, layer, xAxis, yAxis, this.featureState[source.id], this.layersState[layer.id]);
                        break;
                    case 'rectangle':
                        drawRectangleLayer(pass, features, layer, xAxis, yAxis, this.featureState[source.id], this.layersState[layer.id]);
                        break;
                    case 'axis-labels':
                        pass.drawer.endClip();
                        drawAxisLabelsLayer(pass, features, layer, xAxis, yAxis, this.featureState[source.id], this.layersState[layer.id]);
                        pass.drawer.beginClip(state.chartViewport);
                        break;
                    case 'icon':
                        drawIconLayer(pass, features, layer, xAxis, yAxis, this.featureState[source.id], this.layersState[layer.id]);
                        break;
                    case 'text':
                        drawTextLayer(pass, features, layer, xAxis, yAxis, this.featureState[source.id], this.layersState[layer.id]);
                        break;
                    case 'shade':
                        drawShadeLayer(pass, features, layer, xAxis, yAxis, this.featureState[source.id], this.layersState[layer.id]);
                        break;
                    case 'shade-line':
                        pass.drawer.endClip();
                        drawShadeLineLayer(pass, features, layer, xAxis, yAxis, this.featureState[source.id], this.layersState[layer.id]);
                        pass.drawer.beginClip(state.chartViewport);
                        break;
                    case 'axis-indicator':
                        pass.drawer.endClip();
                        // prettier-ignore
                        drawAxisIndicatorLayer(pass, features, layer, xAxis, yAxis, this.featureState[source.id], this.layersState[layer.id]);
                        pass.drawer.beginClip(state.chartViewport);
                        break;
                }
            } else if (source instanceof RasterSource) {
                switch (layer.type) {
                    case 'raster':
                        this.layersState[layer.id] = drawRasterLayer(pass, source, layer, xAxis, yAxis, this.layersState[layer.id]);
                        break;
                    case 'icon-grid':
                        drawIconGridLayer(pass, source, layer, xAxis, yAxis, this.layersState[layer.id]);
                        break;
                }
            }
        }
        pass.drawer.endClip();
        pass.drawer.setIdentityTransform();

        if (import.meta.env.DEV) {
            pass.drawer.drawText(
                frameTimestamp.toFixed(0),
                [pass.viewState.canvasWidth, 0],
                {
                    alignment: 'right',
                    baseline: 'top'
                },
                {
                    color: '#000'
                }
            );
        }
    }

    // Updates scales' origins and lengths according to new chartRect and draws
    private resize(): void {
        const canvas = this.canvas;
        const dpr = window.devicePixelRatio;

        canvas.width = this.container.clientWidth * dpr;
        canvas.height = this.container.clientHeight * dpr;
        this.transform.resize(this.canvas.clientWidth, this.canvas.clientHeight, dpr);
        this.triggerRepaint();
    }

    drag(deltaX: number, deltaY: number) {
        this.transform.drag(deltaX, deltaY);
        this.triggerRepaint();
    }

    resetViewState() {
        this.transform.resetViewState();
        this.triggerRepaint();
    }

    queryFeatures(e: { point: MousePoint; layerIds: string[] }): ChartQueryFeature[] {
        const state = this.transform.getState();
        if (!state.chartViewport.contains(e.point.x, e.point.y)) {
            return [];
        }
        const features = [] as ChartQueryFeature[];
        for (const layerId of e.layerIds) {
            const layer = this.layers.find((x) => x.id === layerId);
            if (!layer) {
                continue;
            }

            const source = this.sources[layer.sourceId];
            if (!source || !(source instanceof VectorSource)) {
                continue;
            }

            const xAxis = this.axes[layer.xAxisId];
            const yAxis = this.axes[layer.yAxisId];
            if (!xAxis || !yAxis) {
                continue;
            }

            const x = xAxis.unproject(e.point.x, state);
            const y = yAxis.unproject(e.point.y, state);

            for (const feature of source.getData()) {
                if (feature.geometry.type === 'Polygon') {
                    // FIXME: this assumes polygon is a rectangle!
                    const x1 = feature.geometry.coordinates[0][0];
                    const y1 = feature.geometry.coordinates[0][1];
                    const x2 = feature.geometry.coordinates[2][0];
                    const y2 = feature.geometry.coordinates[2][1];

                    if (x1 <= x && x <= x2 && y1 <= y && y <= y2) {
                        features.push({ layer, feature });
                    }
                } else if (feature.geometry.type === 'Point') {
                    if ((layer.type === 'axis-labels' || layer.type === 'grid-lines') && layer.layout.axis === 'x') {
                        const x1 = xAxis.unproject(e.point.x - 2, state);
                        const x2 = xAxis.unproject(e.point.x + 2, state);

                        if (x1 <= feature.geometry.coordinates[0] && feature.geometry.coordinates[0] <= x2) {
                            features.push({ layer, feature });
                        }
                    }
                }
            }
        }
        return features;
    }

    private createCanvas(): HTMLCanvasElement {
        const canvas = document.createElement('canvas');
        canvas.style.height = '100%';
        canvas.style.width = '100%';
        const dpr = window.devicePixelRatio;
        canvas.width = this.container.clientWidth * dpr;
        canvas.height = this.container.clientHeight * dpr;
        this.container.appendChild(canvas);
        return canvas;
    }

    private addListeners(canvas: HTMLCanvasElement): void {
        canvas.addEventListener('wheel', this.onWheel);
        canvas.addEventListener('mousedown', this.onMouseDown);
        canvas.addEventListener('mousemove', this.onMouseMove);
        canvas.addEventListener('mouseup', this.onMouseUp);
        canvas.addEventListener('mouseleave', this.onMouseLeave);
        this.mediaQuery.addEventListener('change', this.resize);
    }

    private removeListeners(canvas: HTMLCanvasElement | null | undefined): void {
        canvas?.removeEventListener('wheel', this.onWheel);
        canvas?.removeEventListener('mousedown', this.onMouseDown);
        canvas?.removeEventListener('mousemove', this.onMouseMove);
        canvas?.removeEventListener('mouseup', this.onMouseUp);
        canvas?.removeEventListener('mouseleave', this.onMouseLeave);
        this.mediaQuery.removeEventListener('change', this.resize);
    }

    private applyAxisAutoLimits() {
        for (const id in this.axes) {
            const axis = this.axes[id]!;
            const axisProps = axis.getProps();
            if (axisProps.autoScale !== true) {
                continue;
            }

            const isHorizontal = axis.isHorizontal();

            // Grab all datasets that use this axis
            const uniqueDataIds = this.layers
                .filter((x) => id === (isHorizontal ? x.xAxisId : x.yAxisId))
                .reduce((p, n) => {
                    if (!p.includes(n.sourceId)) {
                        p.push(n.sourceId);
                    }
                    return p;
                }, [] as string[]);

            let max = Math.max(
                0,
                ...uniqueDataIds
                    .map((x) => this.sources[x])
                    .filter((x) => x != null && x instanceof VectorSource)
                    .map((x) => (isHorizontal ? x.getBoundsX() : x.getBoundsY()).max)
            );

            const min = Math.min(
                max,
                ...uniqueDataIds
                    .map((x) => this.sources[x])
                    .filter((x) => x != null && x instanceof VectorSource)
                    .map((x) => (isHorizontal ? x.getBoundsX() : x.getBoundsY()).min)
            );

            if (min >= max) {
                logger.warn('Axis %s: invalid min max values (%d, %d)', id, min, max);
                max = min + 1; // So that we don't throw in axis scale code
                // TODO: make this axis as invalid instead and don't draw layers that depend on it
            }

            if (axisProps.margin && axisProps.marginUnit === 'scaleUnits') {
                axis.setLimits(min - axisProps.margin[0], max + axisProps.margin[1]);
            } else {
                axis.setLimits(min, max);
            }
        }
    }

    private applyAxisMarginAndSize() {
        const margin = {
            left: this.props.margin.left,
            top: this.props.margin.top,
            right: this.props.margin.right,
            bottom: this.props.margin.bottom
        };

        for (const axisId in this.axes) {
            const axis = this.axes[axisId]!;
            const props = axis.getProps();

            if (props.hideWhenNoData) {
                const hasData = this.layers
                    .filter((x) => (x.visible !== false && x.xAxisId === axisId) || x.yAxisId === axisId)
                    .some((x) => this.sources[x.sourceId]?.hasData());
                if (!hasData) {
                    continue;
                }
            }
            margin[props.position] += props.sizePixels ?? 0;
        }

        this.transform.setMargin(margin);
    }

    private onWheel = (e: WheelEvent) => {
        e.preventDefault();
        e.stopPropagation();

        const zoomDelta = Math.min(-e.deltaY * ZOOM_SPEED_PER_PIXEL);
        const midpoint = [e.offsetX, e.offsetY] as [number, number];

        const state = this.transform.getState();
        const currentZoom = this.transform.getTargetZoom() ?? state.zoom;
        const newZoomX = !e.altKey ? Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, currentZoom[0] + zoomDelta)) : currentZoom[0];
        const newZoomY = e.altKey ? Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, currentZoom[1] + zoomDelta)) : currentZoom[1];

        this.transform.zoom([newZoomX, newZoomY], midpoint, { duration: 150 });
        this.triggerRepaint();
    };

    private onMouseDown = (e: MouseEvent) => {
        this.fire('mousedown', e);
        this.mouseIsDown = true;
        this.mouseDragging = false;
    };

    private onMouseMove = (e: MouseEvent) => {
        if (this.mouseIsDown) {
            this.drag(e.movementX, e.movementY);
            this.mouseDragging = true;
        } else {
            this.fire('mousemove', e);
            const state = this.transform.getState();
            let cursor = 'unset';
            if (state.chartViewport.contains(e.offsetX, e.offsetY)) {
                cursor = 'grab';
            }

            if (cursor !== this.canvas.style.cursor) {
                this.canvas.style.cursor = cursor;
            }
            /*
            for (const layer of this.layers) {
                if (layer.type !== 'grid-lines' || layer.visible === false || !layer.paint.hoverColor) {
                    continue;
                }

                const source = this.sources[layer.sourceId];
                if (!source || !(source instanceof VectorSource)) {
                    continue;
                }

                const items = this.queryFeatures({ point: { x: e.offsetX, y: e.offsetY }, layerIds: [layer.id] });
                for (const item of items) {
                    if (item.feature.id) {
                        source.setFeatureState(item.feature.id, { hover: true });
                        this.triggerRepaint();
                    }
                }
            }*/
        }
    };

    private onMouseUp = (e: MouseEvent) => {
        this.mouseIsDown = false;
        this.mouseDragging = false;
        this.fire('click', e);
    };

    private onMouseLeave = () => {
        this.mouseIsDown = false;
        this.mouseDragging = false;
        this.canvas.style.cursor = 'unset';
    };

    private getFont = (size: number | null | undefined, font?: string | null) => {
        return `${size ?? this.props.text.baseSize}px ${font ?? this.props.text.fontName}`;
    };

    finalize() {
        super.finalize();
        this.observer.disconnect();
        this.container.removeChild(this.canvas);
        this.removeListeners(this.canvas);
    }
}
